import {
  FormulaTokenList,
  FormulaTokenListItem,
  FormulaTokenListItemProp,
  FormulaTokenListItemReactElement,
} from '@monorepo/shared/componentsV2/formula';
import CalendarIcon from '@svg/calendar.svg';
import ClockIcon from '@svg/clock.svg';
import HashIcon from '@svg/hash.svg';
import TextIcon from '@svg/text.svg';
import ToggleIcon from '@svg/toggle.svg';
import {
  DatasetPropertyResponse,
  DatasetPropertyType,
  MathUnit,
} from 'mapistry-shared';
import React, { useMemo } from 'react';

const propertyTypesAvailableForFormula = [
  DatasetPropertyType.BOOLEAN,
  DatasetPropertyType.DATE,
  DatasetPropertyType.DATETIME,
  DatasetPropertyType.DATE_RANGE,
  DatasetPropertyType.NUMERIC,
  DatasetPropertyType.TEXT,
  DatasetPropertyType.TIME,
];

const propertyCanBeUsedInFormula = (property: DatasetPropertyResponse) => {
  if (property.type === DatasetPropertyType.DATE_RANGE) {
    return property.hasEverBeenTimeSeriesDate;
  }
  return propertyTypesAvailableForFormula.includes(property.type);
};

const datasetPropertyIcons: Partial<
  Record<DatasetPropertyType, React.ReactElement>
> = {
  [DatasetPropertyType.BOOLEAN]: <ToggleIcon />,
  [DatasetPropertyType.DATE]: <CalendarIcon />,
  [DatasetPropertyType.DATETIME]: <CalendarIcon />,
  [DatasetPropertyType.DATE_RANGE]: <CalendarIcon />,
  [DatasetPropertyType.NUMERIC]: <HashIcon />,
  [DatasetPropertyType.TEXT]: <TextIcon />,
  [DatasetPropertyType.TIME]: <ClockIcon />,
};

const datasetPropertyIconAltTexts: Partial<
  Record<DatasetPropertyType, string>
> = {
  [DatasetPropertyType.BOOLEAN]: 'Boolean',
  [DatasetPropertyType.DATE]: 'Date',
  [DatasetPropertyType.DATETIME]: 'Date & time',
  [DatasetPropertyType.DATE_RANGE]: 'Date range',
  [DatasetPropertyType.NUMERIC]: 'Number',
  [DatasetPropertyType.TEXT]: 'Text',
  [DatasetPropertyType.TIME]: 'Time',
};

interface FormulaStepTokenListProps {
  stepInputDatasetProperties: DatasetPropertyResponse[];
}

export function FormulaStepTokenList({
  stepInputDatasetProperties,
}: FormulaStepTokenListProps) {
  const tokenListItems = useMemo<FormulaTokenListItemProp[]>(() => {
    const rootItems = {
      header: '',
      items: [] as FormulaTokenListItemReactElement[],
    };
    const rootRecordProperties = stepInputDatasetProperties.filter(
      (p) => p.type === DatasetPropertyType.RECORD && !p.parentPropertyId,
    );
    const recordPropertyIdToItemsDict = rootRecordProperties.reduce(
      (acc, p) => ({
        ...acc,
        [p.id]: {
          header: p.name,
          items: [] as FormulaTokenListItemReactElement[],
        },
      }),
      {} as Record<string, FormulaTokenListItemProp>,
    );

    // Assuming that schema properties are ordered
    stepInputDatasetProperties.forEach((p) => {
      if (p.isHidden || !propertyCanBeUsedInFormula(p)) {
        return;
      }

      const label =
        p.type === DatasetPropertyType.NUMERIC && p.units
          ? `${p.fullName} (${MathUnit.getLabel(p.units)})`
          : p.fullName;
      const item = (
        <FormulaTokenListItem
          key={p.fullToken}
          icon={datasetPropertyIcons[p.type]}
          iconAltText={datasetPropertyIconAltTexts[p.type]}
          name={label}
          token={p.fullToken}
        />
      );

      const rootParentId = p.parentPropertyIdChain[0];
      const recordPropertyItems = rootParentId
        ? recordPropertyIdToItemsDict[rootParentId]
        : undefined;
      if (recordPropertyItems) {
        recordPropertyItems.items.push(item);
      } else {
        rootItems.items.push(item);
      }
    });

    const recordPropertiesWithChildren = Object.values(
      recordPropertyIdToItemsDict,
    ).filter(({ items }) => items.length > 0);

    return [rootItems, ...recordPropertiesWithChildren];
  }, [stepInputDatasetProperties]);

  return <FormulaTokenList listItems={tokenListItems} />;
}
